import type { ApiEnvelope, PageMeta } from './types'

// Error de una llamada a la API. `offline` = no llegó a responder (sin cobertura, servidor
// caído): la interfaz lo explica distinto que un error de la API.
export class ApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
		public readonly offline = false
	) {
		super(message)
		this.name = 'ApiError'
	}

	get isNotFound(): boolean {
		return this.status === 404
	}
}

export type QueryParams = Record<string, string | number | boolean | undefined>

export const buildUrl = (baseUrl: string, path: string, params: QueryParams = {}): string => {
	const url = new URL(`${baseUrl}${path}`)
	for (const [key, value] of Object.entries(params)) if (value !== undefined) url.searchParams.set(key, String(value))
	return url.toString()
}

export type ApiResult<T> = { data: T; status: number; message: string; meta?: PageMeta }

// Opciones de una llamada: `token` = sesión del personal (spec `staff-auth`), viaja como Bearer.
export type CallOptions = { token?: string }

type Fetch = (input: string, init?: RequestInit) => Promise<Response>

// Cliente fino de la API /v1: desenvuelve el sobre `{ success, code, message, data }` y
// convierte cualquier fallo en ApiError. Sin React: se prueba y se reutiliza (app móvil).
export const createApiClient = (baseUrl: string, fetchImpl: Fetch = (input, init) => fetch(input, init)) => {
	const call = async <T>(url: string, init?: RequestInit): Promise<ApiResult<T>> => {
		let response: Response
		try {
			response = await fetchImpl(url, init)
		} catch {
			throw new ApiError(0, 'Sin conexión', true)
		}
		let body: ApiEnvelope<T> | null = null
		try {
			body = (await response.json()) as ApiEnvelope<T>
		} catch {
			body = null
		}
		if (!response.ok || !body?.success) {
			throw new ApiError(response.status, body?.message ?? `Error ${response.status}`)
		}
		return { data: body.data, status: response.status, message: body.message, meta: body.meta }
	}

	return {
		get: <T>(path: string, params?: QueryParams, { token }: CallOptions = {}) =>
			call<T>(
				buildUrl(baseUrl, path, params),
				token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
			),
		postForm: <T>(path: string, form: FormData, params?: QueryParams) =>
			call<T>(buildUrl(baseUrl, path, params), { method: 'POST', body: form }),
		// Acción del personal sin cuerpo (aprobar, retirar…) o borrado, siempre con su sesión.
		send: <T>(method: 'POST' | 'DELETE', path: string, { token }: Required<CallOptions>) =>
			call<T>(buildUrl(baseUrl, path), { method, headers: { Authorization: `Bearer ${token}` } }),
		postJson: <T>(path: string, body: unknown) =>
			call<T>(buildUrl(baseUrl, path), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			}),
	}
}

export type ApiClient = ReturnType<typeof createApiClient>
